import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { CompanyUsersTable } from "@/components/users/CompanyUsersTable";
import { useState, useEffect, useCallback } from "react";
import { Shield, Users, Plus, Trash2, UserPlus, Tag, Key } from "lucide-react";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { RemoveFromCompanyDialog } from "@/components/users/RemoveFromCompanyDialog";
import { adminFunctionError } from "@/lib/adminFunctionError";

interface AppUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
}


interface CustomRole {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  hourly_rate: number;
  is_protected?: boolean;
}

interface UserCustomRole {
  id: string;
  user_id: string;
  role_id: string;
}

export default function UserRoleManagement() {
  const { t } = useTranslation("admin");
  const { allowed: isAdmin, isLoading: authLoading } = useCanManageCompany();
  const { isSuperadmin: hasSuperadminRole } = useIsSuperadmin();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "roles" ? "roles" : "users";

  const [users, setUsers] = useState<AppUser[]>([]);
  // Per-company role (user_companies.role) for the active company — source of truth for admin/user here.
  const [companyRoles, setCompanyRoles] = useState<Record<string, "admin" | "user">>({});
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [userCustomRoles, setUserCustomRoles] = useState<UserCustomRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dialogs
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [inviteCompanyId, setInviteCompanyId] = useState<string>("");
  const { id: activeCompanyId, name: activeCompanyName } = useCurrentCompany();

  // Same source as CompanyAdmin.tsx: companies table, ordered by name.
  useEffect(() => {
    supabase
      .from("companies")
      .select("id, name")
      .order("name")
      .then(({ data }) => setCompanies(data || []));
  }, []);

  // Pre-select the active company each time the invite dialog opens.
  useEffect(() => {
    if (showInvite) setInviteCompanyId(activeCompanyId ?? "");
  }, [showInvite, activeCompanyId]);

  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [newRoleRate, setNewRoleRate] = useState<string>("0");
  const [creatingRole, setCreatingRole] = useState(false);

  const [showAssignRole, setShowAssignRole] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [selectedSystemRoles, setSelectedSystemRoles] = useState<string[]>([]);
  const [selectedCustomRoleIds, setSelectedCustomRoleIds] = useState<string[]>([]);

  const [showEditRole, setShowEditRole] = useState(false);
  const [editRole, setEditRole] = useState<CustomRole | null>(null);
  const [editRoleName, setEditRoleName] = useState("");
  const [editRoleDesc, setEditRoleDesc] = useState("");
  const [editRoleRate, setEditRoleRate] = useState<string>("0");

  const [resettingUserId, setResettingUserId] = useState<string | null>(null);

  // Remove-from-company confirmation modal
  const [removeTarget, setRemoveTarget] = useState<{ id: string; email: string } | null>(null);
  const [removingUser, setRemovingUser] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!activeCompanyId) return;
    setIsLoading(true);
    try {
      // Fetch only the active company's users via edge function
      const { data: userData, error: userError } = await supabase.functions.invoke("admin-users", {
        body: { action: "list_company_users", companyId: activeCompanyId },
      });
      if (userError) throw userError;
      if (userData?.error) throw new Error(userData.error);
      setUsers(userData.users || []);

      // Per-company admin/user flag (user_companies.role) for the active company
      const { data: uc } = await supabase
        .from("user_companies")
        .select("user_id, role")
        .eq("company_id", activeCompanyId);
      const map: Record<string, "admin" | "user"> = {};
      (uc || []).forEach((r: any) => { map[r.user_id] = r.role === "admin" ? "admin" : "user"; });
      setCompanyRoles(map);

      // Fetch custom roles
      const { data: cRoles } = await supabase.from("custom_roles").select("*").order("name");
      setCustomRoles((cRoles as CustomRole[]) || []);

      // Fetch user custom role assignments
      const { data: ucRoles } = await supabase.from("user_custom_roles").select("*");
      setUserCustomRoles((ucRoles as UserCustomRole[]) || []);
    } catch (err: any) {
      console.error("Fetch error:", err);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    // Access is gated by <CompanyAdminRoute>; anyone reaching this page may load it.
    fetchAll();
  }, [fetchAll]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  // Access enforced by the route guard in App.tsx.

  // Superadmins implicitly have company-admin rights everywhere, so we show a single
  // "superadmin" badge for them instead of a redundant/contradictory admin|user badge.
  const getUserRoles = (userId: string) => {
    const names = (userCustomRoles
      .filter((r) => r.user_id === userId)
      .map((r) => customRoles.find((cr) => cr.id === r.role_id)?.name)
      .filter(Boolean) as string[]);
    const isSuper = names.includes("superadmin");
    const sys = isSuper ? [] : companyRoles[userId] ? [companyRoles[userId]] : [];
    const custom = names.filter((n) => n !== "superadmin");
    return { sys, custom, isSuper };
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteCompanyId) return;
    setInviting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "invite_to_company", email: inviteEmail.trim(), companyId: inviteCompanyId },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setShowInvite(false);
      fetchAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to invite user");
    } finally {
      setInviting(false);
    }
  };

  // Company-scoped: this only unlinks the user from the active company.
  // Global account deletion stays a platform-level (superadmin) action.
  // Confirmation happens in the RemoveFromCompanyDialog modal, not here.
  const handleRemoveFromCompany = async () => {
    if (!activeCompanyId || !removeTarget) return;
    setRemovingUser(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "remove_user_from_company", userId: removeTarget.id, companyId: activeCompanyId },
      });
      if (error) throw await adminFunctionError(error);
      if (data?.error) throw new Error(data.error);
      toast.success("User removed from company");
      setRemoveTarget(null);
      fetchAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to remove user");
    } finally {
      setRemovingUser(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    setCreatingRole(true);
    try {
      const { error } = await supabase.from("custom_roles").insert({
        name: newRoleName.trim(),
        description: newRoleDesc.trim() || null,
        hourly_rate: parseFloat(newRoleRate) || 0,
      });
      if (error) throw error;
      toast.success(`Role "${newRoleName}" created`);
      setNewRoleName("");
      setNewRoleDesc("");
      setNewRoleRate("0");
      setShowCreateRole(false);
      fetchAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to create role");
    } finally {
      setCreatingRole(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editRole || !editRoleName.trim()) return;
    try {
      const updates: Record<string, unknown> = {
        description: editRoleDesc.trim() || null,
        hourly_rate: parseFloat(editRoleRate) || 0,
      };
      if (!editRole.is_protected) {
        updates.name = editRoleName.trim();
      }
      const { error } = await supabase
        .from("custom_roles")
        .update(updates)
        .eq("id", editRole.id);
      if (error) throw error;
      toast.success("Role updated");
      setShowEditRole(false);
      fetchAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update role");
    }
  };

  const handleUpdateRoleRate = async (roleId: string, rate: number) => {
    try {
      const { error } = await supabase
        .from("custom_roles")
        .update({ hourly_rate: rate })
        .eq("id", roleId);
      if (error) throw error;
      setCustomRoles((prev) =>
        prev.map((r) => (r.id === roleId ? { ...r, hourly_rate: rate } : r))
      );
      toast.success("Gross hourly rate updated");
    } catch (err: any) {
      toast.error(err.message || "Failed to update rate");
    }
  };

  const handleDeleteRole = async (role: CustomRole) => {
    if (!confirm(`Delete role "${role.name}"? All user assignments will be removed.`)) return;
    try {
      const { error } = await supabase.from("custom_roles").delete().eq("id", role.id);
      if (error) throw error;
      toast.success("Role deleted");
      fetchAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete role");
    }
  };

  // Per-company admin flag (user_companies.role) — what RLS actually checks via is_company_admin().
  const setCompanyRole = async (userId: string, role: "admin" | "user") => {
    if (!activeCompanyId) throw new Error("No active company");
    const { data, error } = await supabase.functions.invoke("admin-users", {
      body: { action: "set_company_role", userId, companyId: activeCompanyId, role },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
  };

  const openAssignRoles = (userId: string) => {
    setAssignUserId(userId);
    setSelectedSystemRoles([companyRoles[userId] === "admin" ? "admin" : "user"]);
    setSelectedCustomRoleIds(
      userCustomRoles
        .filter((r) => r.user_id === userId)
        .map((r) => r.role_id)
        .filter((id) => customRoles.find((cr) => cr.id === id)?.name !== "superadmin")
    );
    setShowAssignRole(true);
  };

  // Reconcile: add newly selected, remove deselected. Admin/user lives only in user_companies.role.
  const handleAssignRoles = async () => {
    if (!assignUserId || !activeCompanyId) return;
    try {
      let changes = 0;
      const wasAdmin = companyRoles[assignUserId] === "admin";
      const wantAdmin = selectedSystemRoles.includes("admin");
      if (wasAdmin !== wantAdmin) {
        await setCompanyRole(assignUserId, wantAdmin ? "admin" : "user");
        changes++;
      }
      const current = userCustomRoles
        .filter((r) => r.user_id === assignUserId)
        .map((r) => r.role_id)
        .filter((id) => customRoles.find((cr) => cr.id === id)?.name !== "superadmin");
      for (const roleId of selectedCustomRoleIds) {
        if (current.includes(roleId)) continue;
        const { error } = await supabase.from("user_custom_roles").insert({
          user_id: assignUserId,
          role_id: roleId,
        });
        if (error) throw error;
        changes++;
      }
      for (const roleId of current) {
        if (selectedCustomRoleIds.includes(roleId)) continue;
        const { error } = await supabase
          .from("user_custom_roles")
          .delete()
          .eq("user_id", assignUserId)
          .eq("role_id", roleId);
        if (error) throw error;
        changes++;
      }
      if (changes === 0) {
        toast.info("No changes");
      } else {
        toast.success("Roles updated");
      }
      setShowAssignRole(false);
      setSelectedSystemRoles([]);
      setSelectedCustomRoleIds([]);
      fetchAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update roles");
    }
  };


  // Admins never set a password directly — the user gets a secure, time-limited
  // recovery email and chooses their own new password.
  const handleSendPasswordReset = async (userId: string, email: string) => {
    if (!activeCompanyId) return;
    if (!confirm(`Send a password reset email to ${email}?`)) return;
    setResettingUserId(userId);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: {
          action: "send_password_reset",
          userId,
          companyId: activeCompanyId,
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Password reset email sent to ${email}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to send reset email");
    } finally {
      setResettingUserId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("users.title")}</h1>
          <Badge variant="secondary" className="ml-1">Company-scoped</Badge>
        </div>
        <p className="text-muted-foreground">
          {t("users.subtitle")} <span className="italic">Applies to the active company only.</span>
        </p>
      </div>

      <div className="px-6">
        <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="roles" className="gap-2">
              <Tag className="w-4 h-4" />
              Roles
            </TabsTrigger>
          </TabsList>

          {/* ===== USERS TAB ===== */}
          <TabsContent value="users">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                Company Users ({users.length})
              </h2>
              <Button className="gap-2" onClick={() => setShowInvite(true)}>
                <UserPlus className="w-4 h-4" />
                Invite User
              </Button>
            </div>

            <CompanyUsersTable
              storageKey="settings-users-column-widths"
              users={users.map((u) => ({ ...u, email: u.email || "", roles: getUserRoles(u.id) }))}
              loading={isLoading}
              canRemoveSuper={hasSuperadminRole}
              resettingUserId={resettingUserId}
              onAssignRoles={openAssignRoles}
              onSendReset={handleSendPasswordReset}
              onRemove={setRemoveTarget}
            />
          </TabsContent>

          {/* ===== ROLES TAB ===== */}
          <TabsContent value="roles">
            <div className="space-y-6">
              {/* System Roles */}
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-3">Company Roles</h2>
                <p className="text-sm text-muted-foreground mb-3">
                  Built-in roles that control access within this company. These cannot be deleted.
                </p>
                <div className="space-y-2">
                  {["admin", "user"].map((role) => {
                    const count = Object.values(companyRoles).filter((r) => r === role).length;
                    return (
                      <div
                        key={role}
                        className="bg-card rounded-xl border border-border p-4 flex items-center justify-between"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-primary" />
                            <p className="font-medium text-foreground">{role === "admin" ? "Admin" : "Member"}</p>
                            <Badge variant="outline" className="text-xs">
                              Built-in
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {role === "admin"
                              ? "Manages this company's settings and users"
                              : "Works on this company's appliances"}
                          </p>
                        </div>
                        <span className="text-sm text-muted-foreground">{count} user{count !== 1 ? "s" : ""}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Roles */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Custom Roles</h2>
                    <p className="text-sm text-muted-foreground">
                      Create and manage custom roles for your organization.
                    </p>
                  </div>
                  <Button className="gap-2" onClick={() => setShowCreateRole(true)}>
                    <Plus className="w-4 h-4" />
                    Create Role
                  </Button>
                </div>
                {customRoles.filter((r) => r.name !== "superadmin").length === 0 ? (
                  <div className="bg-card rounded-xl border border-border p-8 text-center text-muted-foreground">
                    No custom roles created yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customRoles.filter((r) => r.name !== "superadmin").map((role) => {
                      const count = userCustomRoles.filter((r) => r.role_id === role.id).length;
                      return (
                        <div
                          key={role.id}
                          className="bg-card rounded-xl border border-border p-4 flex items-center justify-between"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-secondary-foreground" />
                              <p className="font-medium text-foreground">{role.name}</p>
                              <Badge variant="secondary" className="text-xs">
                                Custom
                              </Badge>
                              {role.is_protected && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <Shield className="w-3 h-3" />
                                  Protected
                                </Badge>
                              )}
                            </div>
                            {role.description && (
                              <p className="text-sm text-muted-foreground mt-1">{role.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">
                              €{Number(role.hourly_rate ?? 0).toFixed(2)}/hr
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {count} user{count !== 1 ? "s" : ""}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditRole(role);
                                setEditRoleName(role.name);
                                setEditRoleDesc(role.description || "");
                                setEditRoleRate(String(role.hourly_rate ?? 0));
                                setShowEditRole(true);
                              }}
                            >
                              Edit
                            </Button>
                            {!role.is_protected && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteRole(role)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite User Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Email address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <SearchableSelect
              value={inviteCompanyId || null}
              onChange={(val) => setInviteCompanyId(val ?? "")}
              placeholder="Select a company (required)"
              searchPlaceholder="Search companies…"
              emptyMessage="No companies found"
              items={companies.map((c) => ({ value: c.id, label: c.name }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim() || !inviteCompanyId}>
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={showCreateRole} onOpenChange={setShowCreateRole}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Custom Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Role name (e.g., Technician)"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
            />
            <Input
              placeholder="Description (optional)"
              value={newRoleDesc}
              onChange={(e) => setNewRoleDesc(e.target.value)}
            />
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">
                Gross hourly rate (€/hr)
              </label>
              <Input
                type="number"
                min={0}
                step={0.5}
                placeholder="0"
                value={newRoleRate}
                onChange={(e) => setNewRoleRate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used for future labor cost calculations
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRole(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateRole} disabled={creatingRole || !newRoleName.trim()}>
              {creatingRole ? "Creating..." : "Create Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditRole} onOpenChange={setShowEditRole}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Input
                placeholder="Role name"
                value={editRoleName}
                onChange={(e) => setEditRoleName(e.target.value)}
                disabled={!!editRole?.is_protected}
              />
              {editRole?.is_protected && (
                <p className="text-xs text-muted-foreground mt-1">
                  This role is protected and cannot be renamed or deleted.
                </p>
              )}
            </div>
            <Input
              placeholder="Description (optional)"
              value={editRoleDesc}
              onChange={(e) => setEditRoleDesc(e.target.value)}
            />
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">
                Gross hourly rate (€/hr)
              </label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={editRoleRate}
                onChange={(e) => setEditRoleRate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used for future labor cost calculations
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRole(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={!editRoleName.trim()}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={showAssignRole} onOpenChange={setShowAssignRole}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Roles</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Assigning to: <strong>{users.find((u) => u.id === assignUserId)?.email}</strong>
            </p>

            {assignUserId && getUserRoles(assignUserId).isSuper ? (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-muted-foreground">
                This user is a platform superadmin and always has full access to every company.
                Company roles (Admin / Member) do not apply to them.
              </div>
            ) : (
              /* System Roles */
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Company role</p>
                <div className="space-y-2">
                  {["admin", "user"].map((role) => {
                    const checked = selectedSystemRoles.includes(role);
                    return (
                      <label
                        key={role}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          checked
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name="company-role"
                          checked={checked}
                          onChange={() => setSelectedSystemRoles([role])}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground">{role === "admin" ? "Admin" : "Member"}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {role === "admin" ? "Manages this company's settings and users" : "Works on this company's appliances"}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Roles */}
            {customRoles.length > 0 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Custom Roles</p>
                <div className="space-y-2">
                  {customRoles.filter((role) => role.name !== "superadmin").map((role) => {
                    const checked = selectedCustomRoleIds.includes(role.id);
                    return (
                      <label
                        key={role.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCustomRoleIds((prev) => [...prev, role.id]);
                            } else {
                              setSelectedCustomRoleIds((prev) => prev.filter((r) => r !== role.id));
                            }
                          }}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground">{role.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignRole(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignRoles}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove from company confirmation */}
      <RemoveFromCompanyDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        userEmail={removeTarget?.email ?? ""}
        companyName={activeCompanyName ?? "this company"}
        confirming={removingUser}
        onConfirm={handleRemoveFromCompany}
      />
    </div>
  );
}
